import { useEffect, useState } from "react";
import { ShoppingBag, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = [
    { id: "all", name: "Alle Producten" },
    { id: "bescherming", name: "Bescherming" },
    { id: "reiniging", name: "Reiniging" },
    { id: "interieur", name: "Interieur" },
    { id: "accessoires", name: "Accessoires" },
  ];

  const filteredProducts =
    selectedCategory === "all"
      ? products
      : products.filter((p) => p.category === selectedCategory);

  return (
    <div data-testid="products-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 bg-ug-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-primary text-sm font-bold tracking-widest uppercase">
              Shop
            </span>
            <h1 className="text-4xl sm:text-5xl font-heading font-black uppercase tracking-tighter text-white mt-4">
              Premium Producten
            </h1>
            <p className="text-gray-400 text-lg mt-6">
              Dezelfde professionele producten die wij gebruiken, nu ook voor u beschikbaar.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-ug-black border-b border-white/5 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  data-testid={`product-filter-${cat.id}`}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-5 py-2 text-sm font-bold tracking-wider uppercase transition-colors duration-300 ${
                    selectedCategory === cat.id
                      ? "bg-primary text-white"
                      : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-ug-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">Geen producten gevonden in deze categorie</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => (
                <div
                  key={product.id}
                  data-testid={`product-card-${index}`}
                  className="group bg-ug-paper border border-white/5 hover:border-primary/30 transition-colors duration-500 flex flex-col"
                >
                  {/* Image */}
                  <div className="aspect-square overflow-hidden bg-ug-black">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-primary text-xs font-bold tracking-widest uppercase">
                      {product.category}
                    </span>
                    <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white mt-2">
                      {product.name}
                    </h3>
                    <p className="text-gray-400 text-sm mt-2 line-clamp-2 flex-1">
                      {product.description}
                    </p>
                    <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                      <span className="text-2xl font-heading font-black text-white">
                        €{product.price.toFixed(2)}
                      </span>
                      <span
                        className={`text-xs font-medium px-2 py-1 ${
                          product.in_stock
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {product.in_stock ? "Op voorraad" : "Uitverkocht"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-ug-paper">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-heading font-bold uppercase tracking-wide text-white mb-4">
            Interesse in onze producten?
          </h2>
          <p className="text-gray-400 mb-8">
            Neem contact met ons op voor bestellingen of advies over de juiste producten voor uw auto.
          </p>
          <a href="/contact">
            <Button
              data-testid="products-contact-btn"
              className="bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold px-8 py-4 transition-colors duration-300"
            >
              Contact Opnemen
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
